/** تنسيق التواريخ والأرقام بالعربية مع مراعاة المنطقة الزمنية للشركة. */

const AR_LOCALE = 'ar-SA-u-ca-gregory-nu-latn';

// تخزين مثيلات التنسيق لتفادي إعادة الإنشاء المكلفة على الهواتف والأجهزة المحمولة
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const timeFormatters = new Map<string, Intl.DateTimeFormat>();
const numberFormatter = new Intl.NumberFormat(AR_LOCALE);
const percentFormatter = new Intl.NumberFormat(AR_LOCALE, { style: 'percent', maximumFractionDigits: 1 });

function getDateFormat(timeZone: string): Intl.DateTimeFormat {
  let f = dateFormatters.get(timeZone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(AR_LOCALE, {
        timeZone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      f = new Intl.DateTimeFormat(AR_LOCALE, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    dateFormatters.set(timeZone, f);
  }
  return f;
}

function getDateTimeFormat(timeZone: string): Intl.DateTimeFormat {
  let f = dateTimeFormatters.get(timeZone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(AR_LOCALE, {
        timeZone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      f = new Intl.DateTimeFormat(AR_LOCALE, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    dateTimeFormatters.set(timeZone, f);
  }
  return f;
}

function getTimeFormat(timeZone: string): Intl.DateTimeFormat {
  let f = timeFormatters.get(timeZone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(AR_LOCALE, {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      f = new Intl.DateTimeFormat(AR_LOCALE, { hour: '2-digit', minute: '2-digit' });
    }
    timeFormatters.set(timeZone, f);
  }
  return f;
}

export function formatDate(value: string | Date | null | undefined, timeZone = 'Asia/Riyadh'): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return getDateFormat(timeZone).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function formatDateTime(value: string | Date | null | undefined, timeZone = 'Asia/Riyadh'): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return getDateTimeFormat(timeZone).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatTime(value: string | Date | null | undefined, timeZone = 'Asia/Riyadh'): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return getTimeFormat(timeZone).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatPercent(value: number): string {
  return percentFormatter.format(value);
}

export function relativeTime(value: string | null | undefined, now = Date.now()): string {
  if (!value) return '—';
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return '—';
  const diff = target - now;
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;
  const rtf = new Intl.RelativeTimeFormat('ar', { numeric: 'auto' });
  if (abs < minute) return 'الآن';
  if (abs < hour) return rtf.format(Math.round(diff / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diff / hour), 'hour');
  if (abs < day * 30) return rtf.format(Math.round(diff / day), 'day');
  return formatDate(value);
}

/** تاريخ اليوم بصيغة YYYY-MM-DD في منطقة زمنية محددة. */
export function todayInZone(timeZone = 'Asia/Riyadh'): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
}

export function addDaysLocal(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

export function timeAgo(value: string | null | undefined): string {
  return relativeTime(value);
}

/** للاستخدام في مدخلات التاريخ المحلية. */
export function dateToInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function truncate(value: string, length: number): string {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}
